/**
 * Cockpit nav constants kept in their own file so the shell component file
 * can keep React Fast Refresh working (it only exports components).
 *
 * Phase 4 collapses the original 12-tab sidebar into 6 task-focused tabs.
 * The legacy IDs remain in the union so existing call sites (command
 * palette, deep-link suggestions) keep compiling — `LEGACY_TO_SECTION`
 * maps each legacy ID to its new home.
 */

export type CockpitSectionId =
  // New (Phase 4) consolidated sections
  | 'overview'
  | 'chart'
  | 'plan'
  | 'context'
  | 'intake'
  | 'avs'
  // Legacy IDs — kept so old callers still compile, mapped to new homes
  | 'rooming'
  | 'notes'
  | 'diagnoses'
  | 'rx'
  | 'orders'
  | 'vitals'
  | 'allergies'
  | 'medications'
  | 'immunizations';

/**
 * Tab order used by the 1-9 keyboard shortcuts and the command palette
 * shortcut hints. Lists only the user-visible sections; legacy IDs are
 * resolved through `LEGACY_TO_SECTION` before being passed in.
 */
export const COCKPIT_NAV_ORDER: CockpitSectionId[] = [
  'overview',
  'chart',
  'plan',
  'context',
  'intake',
  'avs',
];

/**
 * Map any legacy section ID to its consolidated home so the command palette
 * (and any other deep-link surfaces) can keep their existing labels and
 * still target a valid tab.
 */
export const LEGACY_TO_SECTION: Record<CockpitSectionId, CockpitSectionId> = {
  // Identity for current sections
  overview: 'overview',
  chart: 'chart',
  plan: 'plan',
  context: 'context',
  intake: 'intake',
  avs: 'avs',
  // Legacy → new
  rooming: 'overview',
  notes: 'chart',
  diagnoses: 'chart',
  rx: 'plan',
  orders: 'plan',
  immunizations: 'plan',
  vitals: 'context',
  allergies: 'context',
  medications: 'context',
};

/** Resolve any (possibly legacy) section ID to a current tab. */
export const resolveSection = (id: CockpitSectionId): CockpitSectionId =>
  LEGACY_TO_SECTION[id] || id;
